import { effect, inject, Injectable } from '@angular/core';
import { I18nService } from './i18n.service';
import { environment } from 'environments/environment';
import { _ } from '@ngx-translate/core';
import { UserInterface } from 'app/models/user.model';
import { LS_FEEDBACK_DIALOG } from 'app/modules/global/localstorage';
import * as Sentry from '@sentry/angular';
import { EnvService } from 'environments/env.service';
import { CurrentUserService } from 'app/core/auth/services/current-user.service';

@Injectable({
  providedIn: 'root',
})
export class SentryService {
  private readonly _i18nService = inject(I18nService);
  private readonly _envService = inject(EnvService);
  private readonly _currentUserService = inject(CurrentUserService);

  // showDialog: boolean = !!JSON.parse(window.localStorage.getItem(LS_FEEDBACK_DIALOG));
  showDialog: boolean = environment.sentry_form;

  ignoreErrors: string[] | RegExp[] = ['ExpressionChangedAfterItHasBeenCheckedError'];

  private _dialogShownTimeout;

  constructor() {
    // Keep Sentry's active user context in sync with the logged-in user for every captured event/replay.
    effect(() => {
      const user = this._currentUserService.currentUser$();
      Sentry.setUser(
        user
          ? { id: user.id, name: user.firstName, surname: user.lastName, username: user.username, email: user.email }
          : null,
      );
    });
  }

  init() {
    // SENTRY TRACKING
    Sentry.init({
      environment: environment.production ? 'prod' : 'dev',
      dsn: environment.sentry_dsn,
      release: environment.changelog,
      integrations: [
        Sentry.browserTracingIntegration(),
        // Block the boot splashscreen and the DocsBot widget from Replay recordings: blocking strips
        // their id/positioning, so a stuck splashscreen no longer renders as a fullscreen overlay.
        Sentry.replayIntegration({ block: ['#fuse-splash-screen', '#docsbotai-root'] }),
      ],
      tracePropagationTargets: [this._envService.baseurl()],
      // Set tracesSampleRate to 1.0 to capture 100%
      // of transactions for performance monitoring.
      // We recommend adjusting this value in production
      tracesSampleRate: 1.0,
      replaysSessionSampleRate: 0.1,
      replaysOnErrorSampleRate: 1.0,
      maxBreadcrumbs: 5,
      // whitelistUrls: [],
      ignoreErrors: this.ignoreErrors,
      beforeSend: (event, hint) => {
        console.log('Before send', event, hint);

        // avoid multiple dialogs
        if (this.showDialog && event.exception) {
          if (this._dialogShownTimeout) {
            clearTimeout(this._dialogShownTimeout);
          }
          this._dialogShownTimeout = setTimeout(() => {
            this.showSentryDialog(event);
          }, 250);
        }

        return event;
      },
    });
  }

  showSentryDialog(event) {
    const user = <UserInterface>JSON.parse(localStorage.getItem('currentUser'));
    let email, name;

    if (user) {
      email = user.email;
      name = user.fullname || user.username;
    }

    return Sentry.showReportDialog({
      eventId: event.event_id,
      title: this._i18nService.translate(_('Ops! Qualcosa è andato storto.')),
      subtitle: this._i18nService.translate(
        _('Puoi inviarci un feedback descrivendo il comportamento che ha causato il problema.'),
      ),
      subtitle2: '',
      labelName: this._i18nService.translate(_('Nome e cognome')),
      labelEmail: this._i18nService.translate(_('Indirizzo email')),
      user: {
        email: email,
        name: name,
      },
      // lang: 'lang',
      labelComments: this._i18nService.translate(_('Cosa è successo?')),
      labelClose: this._i18nService.translate(_('Chiudi')),
      labelSubmit: this._i18nService.translate(_('Invia')),
      errorGeneric: this._i18nService.translate(_('Si è verificato un errore')),
      errorFormEntry: this._i18nService.translate(_('Compila tutti i campi richiesti.')),
      successMessage: this._i18nService.translate(_('Segnalazione inviata con successo. Grazie!')),
    });
  }

  toggleDialog() {
    this.showDialog = !this.showDialog;
    window.localStorage.setItem(LS_FEEDBACK_DIALOG, this.showDialog ? '1' : '0');
  }
}
